import { useEffect, useState } from 'react';
import { type Theme, ThemeProviderContext } from './theme-context';
import { notifyNativeSafeAreaTheme } from '@/lib/native-safe-area-theme';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

export function ThemeProvider({
  children,
  defaultTheme = 'light',
  storageKey = 'milora-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      return (localStorage.getItem(storageKey) as Theme) || defaultTheme;
    } catch {
      return defaultTheme;
    }
  });

  useEffect(() => {
    const root = window.document.documentElement;

    const applyTheme = () => {
      root.classList.remove('light', 'dark');
      if (theme === 'system') {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light';
        root.classList.add(systemTheme);
      } else {
        root.classList.add(theme);
      }
      notifyNativeSafeAreaTheme();
    };

    applyTheme();

    if (theme !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', applyTheme);
    return () => media.removeEventListener('change', applyTheme);
  }, [theme]);

  useEffect(() => {
    notifyNativeSafeAreaTheme();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        notifyNativeSafeAreaTheme();
      }
    };
    const onNativeMessage = (event: MessageEvent) => {
      try {
        const raw = typeof event.data === 'string' ? event.data : '';
        if (!raw.startsWith('{')) return;
        const data = JSON.parse(raw) as { type?: string };
        if (data.type === 'REQUEST_SAFE_AREA_THEME') {
          notifyNativeSafeAreaTheme();
        }
      } catch {
        // ignore non-JSON bridge noise
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('message', onNativeMessage);
    document.addEventListener(
      'message' as keyof DocumentEventMap,
      onNativeMessage as EventListener
    );
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('message', onNativeMessage);
      document.removeEventListener(
        'message' as keyof DocumentEventMap,
        onNativeMessage as EventListener
      );
    };
  }, []);

  const value = {
    theme,
    setTheme: (next: Theme) => {
      try {
        localStorage.setItem(storageKey, next);
      } catch {
        // ignore storage failures
      }
      setTheme(next);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}
